import { Plus, Edit2, Trash2, Loader2, Search, Users, ChevronRight } from "lucide-react";
import { BusinessService } from "../../types/businessService";
import { cn } from "../../lib/utils";
import { Button } from "../ui/Button";
import { useAuth } from "../../auth/AuthContext";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";

interface ServicesPanelProps {
  services: BusinessService[];
  categoryName: string;
  onServiceAdd: () => void;
  onServiceEdit: (service: BusinessService) => void;
  onServiceDelete: (id: string, name: string) => void;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

export const ServicesPanel = ({
  services,
  categoryName,
  onServiceAdd,
  onServiceEdit,
  onServiceDelete,
  isLoading = false,
  error,
  onRetry,
}: ServicesPanelProps) => {
  const { user } = useAuth();
  
  // Helper function to clean and validate currency code
  const getValidCurrencyCode = (code: string | undefined | null): string => {
    if (!code) return "GBP";
    // Remove any trailing numbers or invalid characters, keep only first 3 letters
    const cleaned = code.toString().trim().toUpperCase().substring(0, 3);
    // Validate it's a valid 3-letter code (all letters)
    if (cleaned.length === 3 && /^[A-Z]{3}$/.test(cleaned)) {
      return cleaned;
    }
    return "GBP"; // Fallback to GBP if invalid
  };

  const rawCurrencyCode = user?.company?.currency_code || user?.company?.currency?.currency_code || "GBP";
  const currencyCode = getValidCurrencyCode(rawCurrencyCode);

  const currencyFormatter = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
  });
  return (
    <div className="flex-1 rounded-xl lg:border lg:border-gray-200 lg:shadow-sm bg-transparent lg:bg-white overflow-hidden flex flex-col">
      <div className="flex-1 lg:overflow-y-auto relative p-4 lg:p-0">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100 hidden lg:table-header-group">
            <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
              <th className="col-span-3 text-left font-bold">Service Name</th>
              <th className="col-span-2 text-left font-bold">Category</th>
              <th className="col-span-1 text-left font-bold">Price</th>
              <th className="col-span-1 text-left font-bold">Discount</th>
              <th className="col-span-2 text-left font-bold">Employees</th>
              <th className="col-span-1 text-center font-bold">Status</th>
              <th className="col-span-2 text-right font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="w-full lg:divide-y lg:divide-gray-100 bg-transparent lg:bg-white flex flex-col lg:table-row-group gap-4 lg:gap-0">
            {isLoading ? (
              <tr className="h-64 w-full flex items-center justify-center lg:table-row">
                <td colSpan={12} className="h-full">
                  <div className="flex h-full w-full items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr className="h-64 w-full flex items-center justify-center lg:table-row">
                <td colSpan={12} className="h-full">
                  <div className="flex flex-col h-full w-full items-center justify-center text-center px-6">
                    <p className="text-lg font-semibold text-gray-900 mb-2">
                      Unable to load services
                    </p>
                    <p className="text-gray-500 mb-6">{error}</p>
                    {onRetry && (
                      <button
                        onClick={onRetry}
                        className="px-6 py-2 rounded-lg bg-[#885ABB] text-white font-medium hover:bg-[#6f47a2]"
                      >
                        Try Again
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : services.length === 0 ? (
              <tr className="flex flex-col h-64 text-gray-400 w-full lg:grid lg:grid-cols-12">
                <td className="col-span-12 w-full h-full flex flex-col items-center justify-center">
                  <Search className="w-12 h-12 mb-3 opacity-20 mx-auto" />
                  <p className="text-lg font-medium text-gray-500">
                    No services found
                  </p>
                  <p className="text-sm">Try adjusting your search or filters</p>
                </td>
              </tr>
            ) : (
              <>
                {services.map((service, idx) => (
                  <tr
                    key={service.id}
                    className="flex flex-col lg:grid lg:grid-cols-12 gap-4 items-start lg:items-center px-6 py-5 lg:py-4 hover:bg-purple-50/30 transition-colors bg-white rounded-xl lg:rounded-none border border-gray-200 lg:border-0 shadow-sm lg:shadow-none group text-sm w-full relative"
                  >
                    <td className="lg:col-span-3 min-w-0 w-full lg:w-auto">
                      <div className="font-bold lg:font-semibold text-gray-900 lg:text-gray-800 group-hover:text-[#885ABB] transition-colors truncate text-base lg:text-sm">
                        {service.name}
                      </div>
                      <div className="text-xs text-gray-500 font-medium">
                        {service.durationLabel}
                      </div>
                    </td>

                    <td className="lg:col-span-2 text-gray-700 truncate w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                      <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Category</span>
                      <span className="font-medium lg:font-normal">{service.categoryName || "—"}</span>
                    </td>

                    <td className="lg:col-span-1 font-bold lg:font-medium text-[#885ABB] lg:text-gray-900 w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                      <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Price</span>
                      <span>{currencyFormatter.format(service.price || 0)}</span>
                    </td>

                    <td className="lg:col-span-1 text-gray-700 w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                      <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Discount</span>
                      <span className="font-medium lg:font-normal">
                        {service.discountPrice
                          ? currencyFormatter.format(service.discountPrice)
                          : "—"}
                      </span>
                    </td>

                    <td className="lg:col-span-2 text-gray-700 w-full lg:w-auto flex lg:block justify-between items-start lg:items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0 gap-4">
                      <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase shrink-0 pt-1">Employees</span>
                      <div className="font-medium lg:font-normal text-right lg:text-left break-words lg:truncate flex-1 min-w-0 flex flex-wrap lg:flex-nowrap items-center gap-1">
                        {service.staffNames.length > 0 ? (
                          <>
                            <span className="truncate hidden lg:inline">
                              {service.staffNames.length > 2 ? (
                                <>
                                  {service.staffNames.slice(0, 2).join(", ")}
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button className="ml-1.5 text-[#885ABB] font-bold hover:underline inline-flex items-center gap-0.5">
                                        View all
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-64 p-3" align="start">
                                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                                        <Users className="w-4 h-4 text-[#885ABB]" />
                                        <span className="text-sm font-bold text-[#402B69]">Assigned Employees</span>
                                      </div>
                                      <div className="space-y-1.5 max-h-48 overflow-y-auto custom-scrollbar">
                                        {service.staffNames.map((name, i) => (
                                          <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-purple-50 text-gray-700 text-xs font-medium group transition-colors">
                                            <div className="w-1.5 h-1.5 rounded-full bg-[#885ABB]/40 group-hover:bg-[#885ABB]" />
                                            {name}
                                          </div>
                                        ))}
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                </>
                              ) : (
                                service.staffNames.join(", ")
                              )}
                            </span>
                            <span className="lg:hidden">
                              {service.staffNames.join(", ")}
                            </span>
                          </>
                        ) : (
                          "Unassigned"
                        )}
                      </div>
                    </td>

                    <td className="lg:col-span-1 text-center w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                      <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Status</span>
                      <span
                        className={cn(
                          "inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-semibold border",
                          service.status === "active"
                            ? "bg-[#E8F5E9] text-[#2E7D32] border-[#A5D6A7]"
                            : "bg-gray-100 text-gray-600 border-gray-200"
                        )}
                      >
                        {service.status === "active" ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td className="lg:col-span-2 flex items-center justify-end gap-2 w-full lg:w-auto mt-4 lg:mt-0 pt-4 lg:pt-0 border-t border-gray-100 lg:border-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onServiceEdit(service)}
                        className="flex-1 lg:flex-none h-10 lg:h-8 px-4 lg:px-2 text-blue-600 border-blue-200 hover:bg-blue-50 gap-2 font-bold lg:font-semibold"
                      >
                        <Edit2 size={16} className="lg:w-[15px] lg:h-[15px]" />
                        <span className="lg:hidden">Edit</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onServiceDelete(service.id, service.name)}
                        className="flex-1 lg:flex-none h-10 lg:h-8 px-4 lg:px-2 text-red-600 border-red-200 hover:bg-red-50 gap-2 font-bold lg:font-semibold"
                      >
                        <Trash2 size={16} className="lg:w-[15px] lg:h-[15px]" />
                        <span className="lg:hidden">Delete</span>
                      </Button>
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
